import { createContext, useState, useContext } from 'react'
import { pdfMakeRoutes, reportsRoutes } from '../routes'

const StatusContext = createContext(null)

const downloadFile = async (url, filename) => {
    try {
        const response = await fetch(url)
        if (!response.ok) throw new Error('Network response was not ok')
        const blob = await response.blob()
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(link.href)
    } catch (error) {
        console.error('Download failed:', error)
        throw error
    }
}

const downloadSummaryFile = async (url, filename) => {
    try {
        const response = await fetch(url)
        if (!response.ok) throw new Error('Network response was not ok')
        const blob = await response.blob()
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(link.href)
    } catch (error) {
        console.error('Download failed:', error)
        throw error
    }
}

export const StatusProvider = ({ children }) => {
    const [status, setStatus] = useState({
        status: 'idle',
        reportId: null,
        message: '',
        errorDetails: null,
        isSummary: false
    })

    const initiatePdfFullGeneration = async (report) => {
        console.log('initiatePdfFullGeneration...')
        if (!report || !report._id) {
            console.error('Invalid report provided for PDF generation.')
            return
        }

        try {
            setStatus({
                status: 'generating',
                reportId: report._id,
                message: `Generando PDF para OT ${report.newIdIndex || report.idIndex}. Este proceso se puede tomar un momento...`,
                errorDetails: null,
                isSummary: false
            })

            let sasUrl = report.urlPdf || report.pdfUrl
            let filename = report.pdfDownloadName || report.pdfFileName

            if (sasUrl) {
                try {
                    console.log('[StatusContext] Trying cached urlPdf:', sasUrl)
                    await downloadFile(sasUrl, filename || `OT_${report.newIdIndex || report.idIndex}.pdf`)
                    setStatus({
                        status: 'completed',
                        reportId: report._id,
                        message: `PDF "${filename || 'descargado'}" descargado.`,
                        errorDetails: null,
                        isSummary: false
                    })
                    return
                } catch (cacheError) {
                    console.warn('[StatusContext] Cached urlPdf failed (404 or expired SAS token). Regenerating via server...', cacheError)
                    sasUrl = null
                    report.urlPdf = null
                    report.pdfUrl = null
                    report.pdfDownloadName = null
                }
            }

            console.log('[StatusContext] Requesting fresh PDF generation from server...')
            const payload = { reportData: { _id: report._id } }
            const createPdfResponse = await pdfMakeRoutes.createPdfDoc(payload)
            sasUrl = createPdfResponse.data?.url
            filename = createPdfResponse.data?.downloadName || `OT_${report.newIdIndex || report.idIndex}.pdf`

            if (sasUrl && filename) {
                if (sasUrl) {
                    report.urlPdf = sasUrl
                    try {
                        await reportsRoutes.editReport({
                            _id: report._id,
                            urlPdf: sasUrl
                        })
                    } catch (e) {
                        console.warn('[StatusContext] Failed to persist urlPdf to server:', e)
                    }
                }

                await downloadFile(sasUrl, filename)
                setStatus({
                    status: 'completed',
                    reportId: report._id,
                    message: `PDF "${filename}" descargado.`,
                    errorDetails: null,
                    isSummary: false
                })
            } else {
                throw new Error('La respuesta del servidor no incluyó una URL o nombre de archivo para el PDF.')
            }
        } catch (error) {
            console.error('Error generating PDF:', error)
            const errorDetails = {
                code: error.response?.status || 'Cliente',
                info: error.response?.data?.message || error.message || 'Error desconocido.'
            }
            setStatus({
                status: 'error',
                reportId: report._id,
                message: 'Ocurrió un error al generar el PDF.',
                errorDetails,
                isSummary: false
            })
        }
    }

    const resetStatus = () => {
        setStatus({ status: 'idle', reportId: null, message: '', errorDetails: null })
    }

    const initiatePdfSummaryGeneration = async (report) => {
        console.log('initiatePdfSummaryGeneration...')
        if (!report || !report._id) {
            console.error('Invalid report provided for PDF generation.')
            return
        }
        try {
            setStatus({
                status: 'generating',
                reportId: report._id,
                message: `Generando Resumen de hallazgo. OT ${report.newIdIndex || report.idIndex}...`,
                errorDetails: null,
                isSummary: true
            })
            const response = await pdfMakeRoutes.createSummaryPdf({ _id: report._id })
            const sasUrl = response.data?.url
            const filename = response.data?.downloadName

            if (sasUrl && filename) {
                await downloadSummaryFile(sasUrl, filename)
                setStatus({
                    status: 'completed',
                    reportId: report._id,
                    message: `Resumen PDF "${filename}" descargado.`,
                    errorDetails: null,
                    isSummary: true
                })
            } else {
                throw new Error('La respuesta del servidor no incluyó una URL o nombre de archivo para el resumen PDF.')
            }
        } catch (error) {
            console.error('Error generating PDF summary:', error)
            const errorDetails = {
                code: error.response?.status || 'Cliente',
                info: error.response?.data?.message || error.message || 'Error desconocido.'
            }
            setStatus({
                status: 'error',
                reportId: report._id,
                message: 'Ocurrió un error al generar el resumen PDF.',
                errorDetails,
                isSummary: true
            })
        }
    }

    const initiatePdfNoChecksGeneration = async (report) => {
        console.log('initiatePdfNoChecksGeneration...')
        if (!report || !report._id) {
            console.error('Invalid report provided for PDF generation.')
            return
        }
        try {
            setStatus({
                status: 'generating',
                reportId: report._id,
                message: `Generando Resumen de tareas no ejecutadas. OT ${report.newIdIndex || report.idIndex}...`,
                errorDetails: null,
                isNoChecks: true
            })
            const response = await pdfMakeRoutes.createNoChecksSummaryPdf({ _id: report._id })
            const sasUrl = response.data?.url
            const filename = response.data?.downloadName

            if (sasUrl && filename) {
                await downloadFile(sasUrl, filename)
                setStatus({
                    status: 'completed',
                    reportId: report._id,
                    message: `Resumen de tareas no ejecutadas "${filename}" descargado.`,
                    errorDetails: null,
                    isNoChecks: true
                })
            } else {
                throw new Error('La respuesta del servidor no incluyó una URL o nombre de archivo para el resumen de tareas no ejecutadas.')
            }
        } catch (error) {
            console.error('Error generating PDF no checks:', error)
            const errorDetails = {
                code: error.response?.status || 'Cliente',
                info: error.response?.data?.message || error.message || 'Error desconocido.'
            }
            setStatus({
                status: 'error',
                reportId: report._id,
                message: 'Ocurrió un error al generar el resumen de tareas no ejecutadas.',
                errorDetails,
                isNoChecks: true
            })
        }
    }

    const initiatePdfGeneration = (report) => {
        if (!report || !report._id) {
            console.error('Invalid report provided for PDF generation.')
            return Promise.reject(new Error('Invalid report'))
        }
        if (report.isNoChecks === true) {
            return initiatePdfNoChecksGeneration(report)
        } else if (report.isSummary === true) {
            return initiatePdfSummaryGeneration(report)
        } else {
            return initiatePdfFullGeneration(report)
        }
    }

    const value = {
        status,
        setStatus,
        setstatus: setStatus,
        initiatePdfGeneration,
        resetStatus,
        isSummary: false
    }

    return (
        <StatusContext.Provider value={value}>
            {children}
        </StatusContext.Provider>
    )
}

export const useStatus = () => {
    const context = useContext(StatusContext)
    if (!context) {
        throw new Error('useStatus must be used within a StatusProvider')
    }
    return context
}

export default StatusContext
